import type { ApiResponse } from '@packages/shared-types';

import { apiClient } from '@/services/apiClient';
import { clearAccessToken, setAccessToken } from '@/services/authStorage';
import type { AuthResponse, AuthenticatedUser } from '@/types/auth';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface AcceptInvitationPayload {
  token: string;
  password: string;
  firstName: string;
  lastName: string;
}

export async function login(payload: LoginPayload): Promise<AuthenticatedUser> {
  const response = await apiClient.post<ApiResponse<AuthResponse>>(
    '/api/auth/login',
    payload,
  );
  setAccessToken(response.data.data.accessToken);
  return response.data.data.user;
}

export async function getCurrentUser(): Promise<AuthenticatedUser> {
  const response = await apiClient.get<ApiResponse<AuthenticatedUser>>(
    '/api/auth/me',
  );
  return response.data.data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/api/auth/logout');
  clearAccessToken();
}

export async function acceptInvitation(
  payload: AcceptInvitationPayload,
): Promise<AuthenticatedUser> {
  const response = await apiClient.post<ApiResponse<AuthResponse>>(
    `/api/invitations/${payload.token}/accept`,
    {
      password: payload.password,
      firstName: payload.firstName,
      lastName: payload.lastName,
    },
  );
  setAccessToken(response.data.data.accessToken);
  return response.data.data.user;
}

export async function switchBranch(
  branchId: string,
): Promise<AuthenticatedUser> {
  const response = await apiClient.post<ApiResponse<AuthenticatedUser>>(
    `/api/auth/switch-branch/${branchId}`,
  );
  return response.data.data;
}

export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post<ApiResponse<{ success: boolean }>>(
    '/api/auth/forgot-password',
    { email },
  );
}

export async function resetPassword(
  token: string,
  password: string,
): Promise<void> {
  await apiClient.post<ApiResponse<{ success: boolean }>>(
    '/api/auth/reset-password',
    { token, password },
  );
}
